<!--
  §10 Profile dropdown. Section order, and everything in it, comes from
  `41-profile-panel`: identity, where you are today, desk, appearance, avatar
  style, presence visibility, menu. Nothing that answers "what do I need to
  do?" lives here — that is §10's own test and it is why the day summary,
  today's calendar and the who's-in browser are no longer mounted.

  ── IT IS A POPOVER, AND `data-popover` IS HOW IT SAYS SO ──────────────────

  `POPOVERS.md` §1: seconds, one choice, then gone → popover. This surface was
  built as a full-height right COLUMN in the shell's panel slot, which is the
  other family's rules applied to this one — and §1's opening line is that
  most overlay bugs in this project were exactly that.

  The attribute does three jobs at once (`styles.scss`, the `[data-popover]`
  block): it CONSTRUCTS the plate — fixed, `z-index: 90`, opaque
  `--surface-raised`, radius, border, shadow — it ENROLS this element in the
  band's click-away / Escape / mutual exclusion, and it NAMES the popover for
  the band's close lookup. Presence in the DOM is openness, so there is no
  second flag anywhere that can disagree.

  `role="dialog"`, not `menu` (§5): this holds a radiogroup, a segmented
  control and a switch, not a list of commands. The band focuses this element
  on open, so it is also what carries the accessible name.

  The BOX is the family's one documented exception (§2.4): fixed offsets in
  the SCSS rather than a measured anchor, because the avatar is always the
  last control in the bar. `data-pop-size` is deliberately absent for the
  same reason — the width comes with the offsets.
-->
<div
  class="pp"
  role="dialog"
  data-popover="shell.profile"
  data-companion-avoid
  [attr.aria-label]="'shell.profile_panel.profile_panel' | transloco">

  <!--
    ── Ricoh Paper's profile panel, inside this popover ─────────────────
    `[overlay]="false"` renders Paper's content only (0.40): its close, its
    Appearance block (Light · Graphite · Black, with every other paper behind
    "All themes", COMMON ruling 1), its menu rows and its separated Sign out.
    This popover stays the surface, the dialog and the band member, so
    click-away, Escape (Paper's one order), focus in and back and mutual
    exclusion are the band's, as before. The accent row is off: whether it
    belongs here is a product call, not a swap.

    The app's own sections sit in Paper's slots, in the order they always
    had: who you are and where you are today in `presence` (the identity
    stays the app's, because its avatar opens the photo upload and carries
    the presence dot, which Paper's identity cannot), avatar style and the
    two switches in `extra`, and the rows Paper's action list cannot draw
    (the workspace switcher with its list, Send feedback with its dot,
    Install, Update) in `menu`, after Paper's rows and before Sign out.
  -->
  <paper-profile-panel
    class="pp__paper"
    [overlay]="false"
    [open]="true"
    (openChange)="onPanelOpenChange($event)"
    [identity]="null"
    [showAccent]="false"
    [actions]="menuActions()"
    (action)="onMenuAction($event)">

    <div slot="presence" class="pp__slot">
      <!--
        ── Who you are ───────────────────────────────────────
        RENDERED ONLY FROM A KNOWN IDENTITY. There is no arm of this that paints a
        stand-in name: `?? 'User'` used to put a fabricated identity in the
        viewer's own name slot every time the panel opened ahead of `loadAll()`,
        and permanently whenever the profile query failed. Unknown renders nothing.
        The skeleton is the one exception and it claims one thing only — that a
        fetch is in flight — so it is gated on `identityLoading()`, not on absence.
      -->
      @if (identity(); as id) {
        <header class="pp__id">
          <button
            #avatarButton
            class="pp__avatar"
            type="button"
            (click)="avatarOverlayOpen.set(true)"
            [attr.aria-label]="avatarAriaLabel()"
          >
            <paper-avatar [initials]="id.initials" [image]="profilePicture()" [size]="44" />
            <span
              class="pp__avatar-status"
              [style.background]="currentStatus().colour"
              [attr.aria-label]="statusAriaLabel()"
            ></span>
            <span class="pp__avatar-edit" aria-hidden="true">
              <paper-icon name="photo" [size]="10" />
            </span>
          </button>

          <div class="pp__id-text">
            <h2 class="pp__name">{{ id.name }}</h2>
            @if (id.subtitle) {
              <p class="pp__role">{{ id.subtitle }}</p>
            }
            @if (locationName()) {
              <p class="pp__place">
                <paper-icon name="location-pin" [size]="11" aria-hidden="true" />
                {{ locationName() }}
              </p>
            }
          </div>
        </header>
      } @else if (identityLoading()) {
        <div class="pp__id pp__id--loading" aria-hidden="true">
          <paper-skeleton variant="circle" [width]="44" [height]="44" />
          <div class="pp__id-text">
            <paper-skeleton variant="line" width="120px" [height]="14" />
            <paper-skeleton variant="line" width="80px" [height]="11" />
          </div>
        </div>
      }

      <!-- ── Where you are today ─────────────────────────────── -->
      <section class="pp__block">
        <h3 class="pp__label" id="pp-where-label">{{ copy.whereYouAreToday }}</h3>

        <div class="pp__presence" role="radiogroup" aria-labelledby="pp-where-label">
          <button
            class="pp__tile"
            type="button"
            role="radio"
            [class.is-on]="presence() === 'office'"
            [attr.aria-checked]="presence() === 'office'"
            (click)="pickPresence('office')"
          >
            <paper-icon name="building" [size]="15" aria-hidden="true" />
            <span>{{ 'home.office_today_section.in_office' | transloco }}</span>
          </button>
          <button
            class="pp__tile"
            type="button"
            role="radio"
            [class.is-on]="presence() === 'home'"
            [attr.aria-checked]="presence() === 'home'"
            (click)="pickPresence('home')"
          >
            <paper-icon name="home" [size]="15" aria-hidden="true" />
            <span>{{ 'home.bookings_page.presence_home' | transloco }}</span>
          </button>
          <button
            class="pp__tile"
            type="button"
            role="radio"
            [class.is-on]="presence() === 'away'"
            [attr.aria-checked]="presence() === 'away'"
            (click)="pickPresence('away')"
          >
            <!-- A location pin, as `41-profile-panel` draws it. A moon reads as
                 asleep and collided with the Dark option in Appearance below. -->
            <paper-icon name="location-pin" [size]="15" aria-hidden="true" />
            <span>{{ 'home.bookings_page.presence_away' | transloco }}</span>
          </button>
        </div>

        <!--
          §10: saying "In office" implies a desk, so the panel states WHICH one
          and offers Change. Check in replaces Change while the booking is live
          and unused, because that is the only moment the action exists.

          COMPANION TO A LIT TILE, AND NOTHING ELSE. The `@else` that used to sit
          here rendered "No desk booked" plus a Book-a-desk CTA in EVERY other
          state — including 'home' and 'away', so someone who had just declared
          they were working from home was told they had no desk and pushed to
          book one. `41-profile-panel` draws this row only under a lit In-office
          tile. The office tile itself is still the way to book with nothing
          booked: picking it clears any manual status and goes to the map.
        -->
        @if (presence() === 'office') {
          <div class="pp__desk" (animate.enter)="rowIn($event)" (animate.leave)="rowOut($event)">
            <paper-icon name="desk" [size]="14" aria-hidden="true" />
            <!-- Null name = the booking carries no space, i.e. WHICH desk is not
                 known. The span stays (it is the row's flex spacer) and renders
                 empty, rather than captioning a real booking "No desk booked". -->
            <span class="pp__desk-name">{{ deskName() }}</span>
            @if (currentStatus().canCheckIn) {
              <paper-button intent="affirmative" size="xs" (click)="checkIn()">
                {{ 'shell.profile_panel.sections.right_now.check_in' | transloco }}
              </paper-button>
            } @else {
              <!-- /map is behind `maps.wayfinding` and redirects to the home page
                   when off — an ungated Change closes the panel onto the wrong
                   page. Bound through `flag.*`, NOT a string literal: the
                   directive's input accepts any string, so a typo'd key here
                   would evaluate OFF and delete this CTA silently. See
                   profile-panel-flags.ts. -->
              <paper-button
                *imFeatureFlag="flag.mapsWayfinding"
                intent="ghost"
                size="xs"
                (click)="goToDeskBooking()"
              >
                {{ 'shared.asset_type_picker.change' | transloco }}
              </paper-button>
            }
          </div>
        }

        <!-- /home/my-week is behind `platform.my-week`, same redirect, same
             reason for the constant. -->
        <button
          *imFeatureFlag="flag.platformMyWeek"
          class="pp__week"
          type="button"
          (click)="goToWeek()"
        >
          <paper-icon class="pp__week-mark" name="calendar" [size]="14" aria-hidden="true" />
          <span class="pp__week-text">{{ copy.planTheRestOfYourWeek }}</span>
          <paper-icon class="pp__week-go" name="arrow-right" [size]="13" aria-hidden="true" />
        </button>
      </section>
    </div>

    <div slot="extra" class="pp__slot">
      <!--
        ── Avatar style ──────────────────────────────────────
        `41-profile-panel` draws THREE tiles — Photo · Avatar · GIF — under this
        heading, with the caption "Animated styles pause with reduced motion, and
        hold a still frame in exports and print."

        TWO ARE BUILT AND THE THIRD IS NOT, and that is a decision rather than an
        omission. Photo and Avatar are two readings of ONE field that already
        exists: `picture` set → the photo; `picture` cleared → the initials mark
        every avatar in this app already falls back to. Both tiles therefore do
        exactly what they say, and the tile that is lit is derived from the field
        rather than from a preference nothing writes.

        GIF has no field, no asset pipeline and no animation-pause behaviour
        anywhere in this codebase — `grep avatarStyle|animatedAvatar` over `src/`
        returns nothing, and `ProfileUpdateInput` carries eleven keys, none of
        them this. A third tile would be a control that stores a choice nowhere
        and a caption describing a behaviour that does not exist. The caption is
        omitted with it: "animated styles pause with reduced motion" is an
        assertion about styles this build does not have.

        OWED, and named in this lane's report: `EmployeeInput.avatarStyle`
        ('photo' | 'avatar' | 'gif'), an animated-avatar asset field, and the
        still-frame rule for export and print.
      -->
      <section class="pp__block">
        <h3 class="pp__label">{{ copy.avatarStyle }}</h3>
        <!-- A SEGMENTED CONTROL, not two tiles. `41-profile-panel` draws three
             picture tiles; two of the three have no field behind them (above),
             and a two-way single-select IS a segmented control. It also keeps
             this surface off raw button elements, which the polish ratchet
             counts. It stays Paper's `paper-segmented-control`, square, beside
             the Appearance block's pill switch above: the control has no pill
             appearance yet (a Paper follow-up). NOT DRAWN, and named in the
             report: the reference's avatar previews inside each tile. -->
        <paper-segmented-control
          class="pp__theme"
          [options]="avatarStyleOptions()"
          [value]="avatarStyle()"
          [ariaLabel]="copy.avatarStyle"
          (valueChange)="pickAvatarStyle($event)"
        />
      </section>

      <!--
        ── Visible to colleagues ─────────────────────────────
        Gated on there BEING a viewer. A switch states current state, and with no
        viewer loaded `presenceVisible()` resolved to `true` from a null read —
        the panel asserted "colleagues can see you" without having measured it,
        on a control `onVisibilityChange()` refuses to act on. Its leading rule
        travels with it so the menu keeps exactly one divider above it either way.
      -->
      @if (presenceKnown()) {
        <div class="pp__rule"></div>

        <div class="pp__switch-row">
          <paper-icon class="pp__switch-icon" name="eye" [size]="15" aria-hidden="true" />
          <span class="pp__switch-copy">
            <span class="pp__switch-title">{{ copy.visibleToColleagues }}</span>
            <span class="pp__switch-hint">{{ copy.visibleToColleaguesHint }}</span>
          </span>
          <paper-toggle
            [ngModel]="presenceVisible()"
            (ngModelChange)="onVisibilityChange($event)"
            [disabled]="savingVisibility()"
            [ariaLabel]="copy.visibleToColleagues"
          />
        </div>
      }

      <!--
        ── Ask companion (behind `ask-dock`) ─────────────────
        Nath, 5 Oct: the companion turns on and off from here as well as from its
        own menu, Settings and ⌘K. This device's choice, like those. Its leading
        rule only when the visibility row above did not already draw one.
      -->
      @if (askCompanionOffered()) {
        @if (!presenceKnown()) {
          <div class="pp__rule"></div>
        }
        <div class="pp__switch-row" data-testid="pp-ask-companion">
          <paper-icon class="pp__switch-icon" name="ask-mark" [size]="15" aria-hidden="true" />
          <span class="pp__switch-copy">
            <span class="pp__switch-title">{{ 'shell.ask.companion.label' | transloco }}</span>
            <span class="pp__switch-hint">{{ 'shell.ask.companion.profile_hint' | transloco }}</span>
          </span>
          <paper-toggle
            [ngModel]="askCompanionOn()"
            (ngModelChange)="onAskCompanionChange($event)"
            [ariaLabel]="'shell.ask.companion.label' | transloco"
          />
        </div>
      }
    </div>

    <div slot="menu" class="pp__slot pp__slot--menu">
      <!-- ── Switch workspace — THE ONLY ENTRY POINT ─────────────────────────
           One row naming the current workspace, as in `41-profile-panel`; the
           full list is only worth the height once asked for.

           The shell bar's standalone organisation switcher is gone. It was an
           unlabelled building mark one slot from the location chip's pin, so
           the header offered two building icons for two different nouns —
           which BUILDING you are in, and which CUSTOMER you are signed into.
           Everything that control did lives here now; see the component's
           "Workspace switching" section for the three behaviours that had to
           come with it.
      -->
      @if (switchableCustomers().length > 1) {
        <button
          class="pp__item ph-no-capture"
          type="button"
          [attr.aria-expanded]="useWorkspaceDialog() ? null : workspaceListOpen()"
          [attr.aria-haspopup]="useWorkspaceDialog() ? 'dialog' : null"
          (click)="toggleWorkspaceList()"
          data-testid="pp-switch-workspace"
        >
          <paper-icon name="building" [size]="16" aria-hidden="true" />
          <span class="pp__item-text">{{ 'shell.profile_panel.switch_workspace' | transloco }}</span>
          <!-- Only when the scoped id resolves to an accessible workspace. An
               unresolved id used to render an empty value span. -->
          @if (currentCustomerName(); as workspaceName) {
            <span class="pp__item-value">{{ workspaceName }}</span>
          }
          <paper-icon
            [name]="!useWorkspaceDialog() && workspaceListOpen() ? 'chevron-down' : 'chevron-right'"
            [size]="14"
            aria-hidden="true"
          />
        </button>

        @if (!useWorkspaceDialog() && workspaceListOpen()) {
          <!-- A single-select list of workspaces, so `listbox` / `option` and
               `aria-selected` — not a menu. The search field below is a
               textbox, which is not a legal child of a `menu` either. -->
          <div class="pp__workspaces ph-no-capture" (animate.enter)="rowIn($event)" (animate.leave)="rowOut($event)">
            <!-- Progressive disclosure: the filter appears only past the shared
                 threshold (5). `ph-mask` keeps the typed text out of session
                 replay, and `onWorkspaceQueryChange` keeps it out of analytics —
                 a workspace list is a list of customer names. -->
            @if (showWorkspaceSearch()) {
              <div class="pp__workspace-search">
                <paper-icon name="search" [size]="14" aria-hidden="true" />
                <input
                  type="text"
                  class="ph-mask"
                  [value]="workspaceQuery()"
                  (input)="onWorkspaceQueryChange($any($event.target).value)"
                  [placeholder]="'shell.app_shell.search_organisations' | transloco"
                  [attr.aria-label]="'shell.app_shell.search_organisations' | transloco"
                  autocomplete="off" />
              </div>
            }

            <div
              role="listbox"
              [attr.aria-label]="'shell.app_shell.organisations' | transloco">
              @for (c of filteredWorkspaces(); track c.id) {
                <button
                  class="pp__item pp__item--sub"
                  type="button"
                  role="option"
                  [attr.aria-selected]="c.id === currentCustomerId"
                  [disabled]="c.id === currentCustomerId || switchingCustomerId() !== null"
                  (click)="switchCustomer(c.id)"
                >
                  <!-- The workspace's own mark when it has one. A logo that
                       fails to load falls back to the building icon, never to
                       the browser's broken-image glyph. -->
                  @if (c.logo && !workspaceLogoErrors()[c.id]) {
                    <span class="pp__workspace-logo-frame" aria-hidden="true">
                      <img class="pp__workspace-logo" [src]="c.logo" alt=""
                        loading="lazy" (error)="onWorkspaceLogoError(c.id)" />
                    </span>
                  }
                  <span class="pp__item-text">{{ c.name }}</span>
                  <!-- Demo/test workspaces are labelled, so an internal user
                       always knows they are about to enter synthetic data. -->
                  @if (c.isTest) {
                    <span class="pp__workspace-tag">{{ 'shell.app_shell.demo' | transloco }}</span>
                  }
                  @if (c.id === currentCustomerId) {
                    <paper-icon name="check" [size]="14" aria-hidden="true" />
                  } @else if (switchingCustomerId() === c.id) {
                    <span class="pp__spinner" aria-hidden="true"></span>
                  }
                </button>
              }
            </div>

            @if (showWorkspaceSearch() && filteredWorkspaces().length === 0) {
              <div class="pp__workspace-empty">
                {{ 'shell.app_shell.no_organisations_match' | transloco }}
              </div>
            }
            <!-- A failed switch says why (the API's own words), instead of the
                 row quietly going back to how it was. -->
            @if (platformCustomers.switchError(); as switchError) {
              <div class="pp__workspace-error" role="alert" (animate.enter)="rowIn($event)" (animate.leave)="rowOut($event)">
                <paper-icon name="alert-triangle" [size]="14" aria-hidden="true" />
                <span>{{ 'shell.app_shell.switch_failed' | transloco: { reason: switchError.message } }}</span>
              </div>
            }
          </div>
        }
      }

      <!-- Help is entitled per customer (help.centre). Get Help opens Messenger,
           so it shows only once the API has said Messenger is ready: never on
           the desktop, not before privacy signs it off, and never when the
           customer has Contact support off (help.support). -->
      <ng-container *imFeatureFlag="flag.helpCentre">
        <button type="button" class="pp__item ph-no-capture" data-testid="employee.help.open" (click)="openHelp()">
          <paper-icon name="book-open" [size]="16" aria-hidden="true" />
          <span class="pp__item-text">{{ 'help.title' | transloco }}</span>
          <paper-tag class="pp__beta" size="xs" intent="accent" data-testid="employee.help.open.beta">{{ 'shared.beta' | transloco }}</paper-tag>
          <paper-icon name="chevron-right" [size]="14" aria-hidden="true" />
        </button>

        @if (helpSupport.supportOffered() && helpSupport.messengerOffered()) {
          <button type="button" class="pp__item ph-no-capture" data-testid="employee.help.messenger.open" (click)="getHelp()" [disabled]="helpMessenger.loading()">
            <paper-icon name="message-circle" [size]="16" aria-hidden="true" />
            <span class="pp__item-text">{{ (helpMessenger.loading() ? 'help.support_loading' : 'help.get_help') | transloco }}</span>
            <paper-icon name="chevron-right" [size]="14" aria-hidden="true" />
          </button>
        }
      </ng-container>

      <!-- Send feedback — the floating Feedback button's place now (4 Oct 2026),
           for the same viewers (never on a phone). It carries the What's New
           dot the button used to; the panel opens where this menu was. -->
      @if (feedbackOffered()) {
        <button class="pp__item ph-no-capture" type="button" data-testid="pp-send-feedback" (click)="openFeedback()">
          <paper-icon name="message-circle" [size]="16" aria-hidden="true" />
          <span class="pp__item-text">
            {{ 'shared.feedback.send_feedback' | transloco }}
            @if (releases.hasUnread()) {
              <span class="pp__item-hint">{{ whatsNewHint() }}</span>
            }
          </span>
          @if (releases.hasUnread()) {
            <span class="pp__item-dot" aria-hidden="true"></span>
          }
        </button>
      }

      <!-- PWA install — only while the browser holds a deferred prompt. -->
      @if (pwaInstall.canInstall()) {
        <button class="pp__item ph-no-capture" type="button" (click)="installApp()">
          <paper-icon name="download" [size]="16" aria-hidden="true" />
          <span class="pp__item-text">{{ 'shell.profile_panel.install_app' | transloco }}</span>
        </button>
      }

      <!--
        Update action ONLY. The `@else` that used to sit here was an
        always-rendered static "App version · 4f9a2c31" row, which made a build
        number the last thing above Sign out — `41-profile-panel` draws no such
        row. An update that is genuinely ready is a different thing: it is an
        action, it is transient, and it is the one moment a build identity is
        worth a line of a profile menu.
      -->
      @if (appUpdate.updateAvailable()) {
        <button class="pp__item ph-no-capture" type="button" (click)="applyAppUpdate()">
          <paper-icon name="refresh" [size]="16" aria-hidden="true" />
          <span class="pp__item-text">
            {{ 'shell.profile_panel.app_version_update_available' | transloco }}
          </span>
        </button>
      }

    </div>
  </paper-profile-panel>

  <!-- ── Avatar overlay (photo upload) ───────────────────── -->
  @if (avatarOverlayOpen()) {
    <div class="pp-avatar-overlay" (click)="avatarOverlayOpen.set(false)"
      (animate.enter)="avatarDialogEnter($event)" (animate.leave)="avatarDialogLeave($event)">
      <div class="pp-avatar-modal" role="dialog" aria-modal="true"
        (click)="$event.stopPropagation()">
        <div class="pp-avatar-preview">
          @if (profilePicture()) {
            <img [src]="profilePicture()" [alt]="'shell.profile_panel.profile' | transloco" />
          } @else if (identity(); as id) {
            <span class="pp-avatar-preview-initials">{{ id.initials }}</span>
          }
        </div>
        @if (userName(); as name) {
          <div class="pp-avatar-modal-name">{{ name }}</div>
        }
        <div class="pp-avatar-modal-actions">
          @if (uploadingPhoto()) {
            <div class="pp-upload-progress">
              <span class="pp-upload-dot"></span>
              {{ 'shell.profile_panel.uploading' | transloco }}
            </div>
          } @else {
            <paper-button intent="secondary" size="sm" icon="photo" iconPosition="left"
              (click)="triggerFileUpload()">
              {{ 'shell.profile_panel.change_photo' | transloco }}
            </paper-button>
          }
          <input id="pp-file-input" class="pp-file-input" type="file" accept="image/*"
            (change)="onFileSelected($event)" />
        </div>
        <button class="pp-avatar-modal-close" type="button"
          (click)="avatarOverlayOpen.set(false)" [attr.aria-label]="'shell.profile_panel.close' | transloco">
          <paper-icon name="close" [size]="14" />
        </button>
      </div>
    </div>
  }

</div>
